import { Layout } from '@/components/ui/layout';
import { usersColumns } from './table/columns';
import { UsersDataTable } from './table/data-table';
import { PrivateAdminRoute } from '@/components/organisms/route/admin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import type { UserProfile, UserProfilesPageResponse } from '@/graphql/types/admin';
import { UsersTableLoading } from './table/loading';
import { Alert } from '@/components/molecules/alert';
import { useAlert } from '@/hooks/useAlert';
import { QUERY_USER_PROFILES_PAGE } from '@/graphql/queries/users';
import { EditUserDialog } from './edit-user-dialog';
import type { EditUserFormValues } from './edit-user-dialog';
import { MUTATION_UPDATE_USER } from '@/graphql/mutation/user';
import type { UpdateUserInput } from '@/graphql/types/user';
import { Ellipsis } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TOKEN_KEY } from '@/context/auth';
import { downloadAdminUsersCsv } from '@/services/admin-users/downloadAdminUsersCsv';
import type { AdminUsersExportCsvErrorKind } from '@/services/admin-users/fetchAdminUsersExportCsv';

function getIdTokenFromStorage(): string | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as string;
  } catch {
    return null;
  }
}

function messageForExportError(kind: AdminUsersExportCsvErrorKind): string {
  switch (kind) {
    case 'unauthorized':
      return 'Sessão inválida ou expirada. Faça login novamente.';
    case 'forbidden':
      return 'Sem permissão para exportar.';
    case 'not_found':
      return 'Exportação não encontrada.';
    default:
      return 'Falha ao exportar. Tente novamente.';
  }
}

export default function UsersPage() {
  const apolloClient = useApolloClient();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [data, setData] = useState<UserProfile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    setPagination((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.pageSize !== prev.pageSize) {
        return { pageIndex: 0, pageSize: next.pageSize };
      }
      return next;
    });
  };
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const normalizedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      actions: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(item);
                setOpenEditDialog(true);
              }}
            >
              <span>Editar</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }));
  }, [data]);

  const fetchUserPage = useCallback(async () => {
    setLoadingData(true);
    try {
      const offset = pagination.pageIndex * pagination.pageSize;
      const { data: res } = await apolloClient.query<UserProfilesPageResponse>({
        query: QUERY_USER_PROFILES_PAGE,
        variables: {
          limit: pagination.pageSize,
          offset,
          search: '',
        },
        fetchPolicy: 'network-only',
      });
      setData(res.userProfiles);
      setTotalCount(res.userProfilesTotal);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingData(false);
    }
  }, [apolloClient, pagination.pageIndex, pagination.pageSize, showAlert]);

  useEffect(() => {
    void fetchUserPage();
  }, [fetchUserPage]);

  const handleEditSubmit = async (values: EditUserFormValues) => {
    if (!selectedUser) return;
    setLoadingForm(true);
    try {
      const input: UpdateUserInput = {
        uid: selectedUser.id,
        email: values.email,
        display_name: values.display_name || undefined,
        phone_number: values.phone_number || undefined,
        disabled: values.disabled,
        plan_id: values.plan_id || undefined,
      };

      await apolloClient.mutate<{ updateUser: { user: unknown } }>({
        mutation: MUTATION_UPDATE_USER,
        variables: { input },
      });

      setOpenEditDialog(false);
      setSelectedUser(null);
      showAlert('success', 'Usuário atualizado com sucesso');

      await fetchUserPage();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleExportCsv = useCallback(async () => {
    const token = getIdTokenFromStorage();
    if (!token) {
      showAlert('error', messageForExportError('unauthorized'));
      return;
    }
    setExportLoading(true);
    try {
      const result = await downloadAdminUsersCsv(token);
      if (!result.ok) {
        showAlert('error', messageForExportError(result.kind));
        return;
      }
      showAlert('success', 'Arquivo CSV baixado com sucesso');
    } finally {
      setExportLoading(false);
    }
  }, [showAlert]);

  return (
    <PrivateAdminRoute>
      <Layout title="Usuários">
        {loadingData ? (
          <UsersTableLoading />
        ) : (
          <UsersDataTable
            columns={usersColumns}
            data={normalizedData}
            rowCount={totalCount}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            onExportCsv={handleExportCsv}
            exportLoading={exportLoading}
          />
        )}
      </Layout>

      <EditUserDialog
        open={openEditDialog}
        onOpenChange={(open) => {
          setOpenEditDialog(open);
          if (!open) setSelectedUser(null);
        }}
        onSubmit={handleEditSubmit}
        loading={loadingForm}
        initialData={selectedUser}
      />

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PrivateAdminRoute>
  );
}
